import { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button, ButtonWithPopover, Icon } from '@seeqdev/qomponents';
import { Checkbox } from '@/core/Checkbox.atom';
import { useFlux } from '@/core/hooks/useFlux.hook';
import ValueWithUnits from '@/trend/ValueWithUnits.atom';
import { humanizeInterval } from '@/datetime/dateTime.utilities';
import { sqDurationStore } from '@/core/core.stores';
import { AUTO_UPDATE } from '@/trendData/trendData.constants';
import { autoUpdate as autoUpdateDurationActions } from '@/trendData/duration.actions';
import { setChartWidth } from '@/trendData/trend.actions';
import { DURATION_TIME_UNITS_ALL } from '@/main/app.constants';
import type { FrontendDuration } from '@/services/systemConfiguration.types';

export const AutoUpdate: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const humanizeAutoInterval = () => {
    return t('AUTO_UPDATE.AUTO_INTERVAL', {
      autoInterval: humanizeInterval(autoUpdate.autoInterval),
    });
  };

  const [showAutoUpdateModal, setShowAutoUpdateModal] = useState(false);
  const [invalidManualInterval, setInvalidManualInterval] = useState(false);
  const timeoutInstance = useRef<number | null>(null);
  const { autoUpdate } = useFlux(sqDurationStore);

  // intentionally added into setState in order to not call humanizeInterval on each rerender
  const [autoInterval, setAutoInterval] = useState(humanizeAutoInterval());

  useEffect(() => {
    if (!sqDurationStore.displayPixels) {
      setChartWidth();
    }
  }, []);

  useEffect(() => {
    setAutoInterval(humanizeAutoInterval());
  }, [autoUpdate.autoInterval, showAutoUpdateModal]);

  const closeAutoUpdateModal = () => setShowAutoUpdateModal(false);

  const setMode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value as typeof AUTO_UPDATE.MODES[keyof typeof AUTO_UPDATE.MODES];
    autoUpdate.mode !== newValue && autoUpdateDurationActions.setMode(newValue);
  };

  const isInvalidManualInterval = (interval: FrontendDuration) => {
    return (
      !_.isNumber(interval.value) ||
      interval.value <= 0 ||
      (interval.units === 's' && interval.value < AUTO_UPDATE.MIN_INTERVAL / 1000)
    );
  };

  const onManualIntervalChange = (interval: FrontendDuration) => {
    const isInvalid = isInvalidManualInterval(interval);
    autoUpdate.mode !== AUTO_UPDATE.MODES.MANUAL && autoUpdateDurationActions.setMode(AUTO_UPDATE.MODES.MANUAL);
    timeoutInstance.current && window.clearTimeout(timeoutInstance.current);
    if (isInvalid) {
      // Delay setting error so user has opportunity to clear field and type new number without error message
      timeoutInstance.current = window.setTimeout(() => {
        setInvalidManualInterval(isInvalid);
      }, 1000);
      return;
    }
    setInvalidManualInterval(false);
    autoUpdateDurationActions.setManualInterval(interval);
  };

  const getManualCustomDescription = () => (
    <span className="manualIntervalContainer pl10">
      <ValueWithUnits
        extraClassNames="mt0 mb5 ml0"
        onChange={(item) => onManualIntervalChange(item as FrontendDuration)}
        min={1}
        defaultValue={autoUpdate.manualInterval}
        required={true}
        availableUnits={DURATION_TIME_UNITS_ALL}
      />
    </span>
  );

  const isOffMode = autoUpdate.mode === AUTO_UPDATE.MODES.OFF;

  return (
    <div className="cursorPointer flexColumnContainer mt2 ml5">
      <ButtonWithPopover
        isOpen={showAutoUpdateModal}
        placement="top"
        hasArrow
        isCloseOnContentClick={false}
        onOpenChange={setShowAutoUpdateModal}
        trigger={
          <div data-testid="autoUpdateButton" className="flexColumnContainer flexAlignCenter tw:pb-1">
            <Icon
              testId="autoUpdateButtonIcon"
              size="lg"
              icon={clsx({
                'fc-refresh-off': isOffMode,
                'fc-refresh-on': !isOffMode,
              })}
              tooltip={t('AUTO_UPDATE.HEADER')}
              tooltipPlacement="bottom"
              extraClassNames="flexCenter"
              tooltipDelay={1500}
            />
          </div>
        }>
        <div id="autoUpdatePopover" data-testid="autoUpdatePopover" className="autoUpdatePopup p5">
          <div className="popover-header tw:h-8 tw:flex tw:items-center tw:justify-center">
            <span>{t('AUTO_UPDATE.HEADER')}</span>
            <Icon
              icon="fa-question-circle"
              extraClassNames="text-interactive pl5"
              size="lg"
              tooltip={t('AUTO_UPDATE.EXPLANATION')}
            />
          </div>
          <div>
            <div className="picker-close" onClick={closeAutoUpdateModal}>
              <Icon icon="fa-close cursorPointer" />
            </div>
            <div className="flexRowContainer autoUpdateOptions">
              <Checkbox
                id="autoUpdate"
                label={t('AUTO_UPDATE.AUTO')}
                extraLabel={
                  <span className="pl5" data-testid="autoInterval">
                    {autoInterval}
                  </span>
                }
                classes="flexRowContainer mt5 mb10 ml10 width-auto flexAlignStart"
                descriptionClasses="width-auto ml10 mr10 mt5 small"
                type="radio"
                value={AUTO_UPDATE.MODES.AUTO}
                description={t('AUTO_UPDATE.AUTO_DESCRIPTION')}
                isChecked={autoUpdate.mode === AUTO_UPDATE.MODES.AUTO}
                onChange={setMode}
              />
              <span>
                <Checkbox
                  id="updateManual"
                  label={t('AUTO_UPDATE.MANUAL')}
                  extraLabel={getManualCustomDescription()}
                  classes="flexRowContainer mt5 mb0 ml10 flexAlignStart updateManualContainer"
                  descriptionClasses="width-auto ml10 mr10 mt5 small"
                  type="radio"
                  value={AUTO_UPDATE.MODES.MANUAL}
                  description={t('AUTO_UPDATE.MANUAL_DESCRIPTION')}
                  isChecked={autoUpdate.mode === AUTO_UPDATE.MODES.MANUAL}
                  onChange={setMode}
                />
                {invalidManualInterval && (
                  <div className="ml20 small sq-fairly-dark-gray text-italic errorMsg">
                    {t('AUTO_UPDATE.INVALID_MANUAL_INTERVAL')}
                  </div>
                )}
              </span>
              <Checkbox
                id="updateOff"
                label={t('AUTO_UPDATE.OFF')}
                classes="flexRowContainer mt15 mb10 ml10 width-auto flexAlignStart"
                descriptionClasses="width-auto ml10 mr10 mt5 small"
                type="radio"
                value={AUTO_UPDATE.MODES.OFF}
                description={t('AUTO_UPDATE.OFF_DESCRIPTION')}
                isChecked={isOffMode}
                onChange={setMode}
              />
            </div>
          </div>
        </div>
      </ButtonWithPopover>
    </div>
  );
};
